/*
 * @Author: lijing 
 * @Date: 2022-08-25 16:49:13 
 * @Last Modified by: lijing
 * @Last Modified time: 2022-11-03 17:11:47
 */
import React, { FC, createContext, useState } from "react";
import { message, Tabs } from 'antd';
import Country from "./components/Country";
import Province from './components/Province';
import Hospital from "./components/Hospital";
import Subject from "./components/Subject";
import Center from "./components/Center";
import { useSelector } from "react-redux";
import { RootState } from "@root/store";
const { TabPane } = Tabs;

// 标签页数据类型
interface TabProps {
  key: string,
  name: string,
  Component: FC
}

// 合计内容数据类型
export interface TotalViewProps {
  courseTag: string,
  courseCount: number,
  courseDuration: number,
  watchUserCount: number,
  watchTime: number,
  averageWatchTime: number,
  recipientHospitalCourseCount?: number,
  supportHospitalCourseCount?: number
}

// 日期数据类型
export interface DateProps {
  year?: string,
  month?: string
}

// 按医院查询默认传参 
export interface HospitalSelected {
  provinceCode: string,
  cityCode: string
}

// 导出context类型
interface IStatisticsContext {
  baseUserCategory: string,
  hospitalSelected?: HospitalSelected | undefined,
  excelDownload?: (data: any) => void,
  getUrlParams?: (form: any) => string,
  setkey?: (index: string, hospitalSelected?: HospitalSelected) => void
}

// 课程统计标签页
const countryTabs: TabProps[] = [
  {
    key: '1',
    name: '按课程标签查询',
    Component: Country
  },
  {
    key: '2',
    name: '按省查询',
    Component: Province
  }
]
const hospitalTabs: TabProps[] = [
  {
    key: '3',
    name: '按医院查询',
    Component: Hospital
  }
]
const defaultTabs: TabProps[] = [
  {
    key: '4',
    name: '按学科查询',
    Component: Subject
  }
]
const centerTabs: TabProps[] = [
  {
    key: '5',
    name: '按中心查询',
    Component: Center
  }
]

// 导出：content,
export const StatisticsContext = createContext<IStatisticsContext>({ baseUserCategory: '12' });

const CourseStatistics: FC = () => {
  const [hospitalSelected, setHospitalSelected] = useState<HospitalSelected | undefined>()

  // 取值：当前角色
  const { baseUserCategory } = useSelector(
    (state: RootState) => state.userInfo
  );

  /**
   * description：判断不同角色下显示的标签内容
   * (tabLists) 标签数组
   * 
   * 国家级：按课程标签查询、按省查询、按医院查询、按学科查询、按中心查询
   * 省市级：按医院查询、按学科查询
   * 支受援医院：按学科查询
   */
  let tabLists: TabProps[] = [];
  switch (baseUserCategory) {
    case '10':
      tabLists = [...countryTabs, ...hospitalTabs, ...defaultTabs, ...centerTabs];
      break;

    case '11':
    case '15':
      tabLists = [...hospitalTabs, ...defaultTabs];
      break;

    default:
      tabLists = [...defaultTabs];
      break;
  }

  // 展开标签页，默认第一页
  const [key, setKey] = useState(tabLists[0].key);

  /**
   * 将后端返回的流文件进行下载
   * 
   * @param data 返回的bold文件 
   * @returns void
   */
  const excelDownload = (data: any) => {
    if (data?.size === 0) {
      message.error('没有数据可以导出');
      return false;
    }

    let blob = new Blob([data], {
      type: 'application/octet-stream'
    }); // 转化为blob对象
    let filename = '课程统计数据导出.xlsx'; // 判断是否使用默认文件名

    var blobURL = window.URL.createObjectURL(blob); // 将blob对象转为一个URL
    var tempLink = document.createElement('a'); // 创建一个a标签
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename); // 给a标签添加下载属性
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank');
    }
    document.body.appendChild(tempLink); // 将a标签添加到body当中
    tempLink.click(); // 启动下载
    document.body.removeChild(tempLink); // 下载完毕删除a标签
    window.URL.revokeObjectURL(blobURL);
  }

  /**
   * 将object对象转为url参数
   * 
   * @param form 
   * @returns str(key=value&)
   */
  const getUrlParams = (form: any) => {
    let str = '';
    for (let i in form) {
      if (str !== '') str += '&';
      if (form[i])    str += `${i}=${form[i]}`;
    }
    return str;
  }

  /**
   * 设置标签页
   * 
   * @param index 跳转页面的key
   * @param hospitalSelected 按省查询 => 点击所在市 => 跳转至按医院查询时所附带的省、市参数
   * @return 
   */
  const onSetKey = (index: string, hospitalSelected?: HospitalSelected) => {
    if (hospitalSelected) {
      setHospitalSelected(hospitalSelected);
    }

    setKey(index);
  }

  const passedContext: IStatisticsContext = {
    baseUserCategory: baseUserCategory as string,
    hospitalSelected: hospitalSelected,
    excelDownload: excelDownload,
    getUrlParams: getUrlParams,
    setkey: onSetKey
  }

  return (
    <div className="coursestatistics">
      <StatisticsContext.Provider value={passedContext}>
        <Tabs activeKey={key} onChange={onSetKey}>
          {
            tabLists.map((tab, index) => {
              return (
                <TabPane tab={tab.name} key={tab.key}>
                  <tab.Component />
                </TabPane>
              )
            })
          }
        </Tabs>
      </StatisticsContext.Provider>
    </div>
  );
};

export default CourseStatistics;
